import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { formatNumber, formatPercent } from '@/lib/format'

export interface CohortRow {
    key: string
    label: string
    size: number
    retention: number[]
}

interface CohortHeatmapProps {
    rows: CohortRow[]
    color?: string
}

type HoverState = {
    row: number
    column: number
    x: number
    top: number
    bottom: number
    below: boolean
}

const DOMAIN_MIN = 60
const TOOLTIP_HALF_WIDTH = 125

// Sequential single hue: lighter means fewer customers retained. The mix stays below 68%
// so the text inside every cell keeps its contrast in light and dark mode.
const cellBackground = (value: number, color: string): string => {
    const ratio = Math.max(0, Math.min(1, (value - DOMAIN_MIN) / (100 - DOMAIN_MIN)))
    const mix = 8 + ratio * 60
    return `color-mix(in oklch, ${color} ${mix.toFixed(1)}%, var(--card))`
}

export const CohortHeatmap: FC<CohortHeatmapProps> = ({ rows, color = 'var(--chart-2)' }): ReactNode => {
    const wrapperRef = useRef<HTMLDivElement>(null)
    const [hover, setHover] = useState<HoverState | null>(null)
    const columns = rows.reduce((max, row) => Math.max(max, row.retention.length), 0)
    const hovered = hover ? rows[hover.row] : null
    const hoveredValue = hover && hovered ? hovered.retention[hover.column] : undefined

    const onEnter = (element: HTMLElement, row: number, column: number): void => {
        const wrapper = wrapperRef.current
        if (!wrapper) return

        const cell = element.getBoundingClientRect()
        const box = wrapper.getBoundingClientRect()
        const center = cell.left - box.left + cell.width / 2
        const x = Math.max(TOOLTIP_HALF_WIDTH, Math.min(box.width - TOOLTIP_HALF_WIDTH, center))

        setHover({
            row,
            column,
            x,
            top: cell.top - box.top,
            bottom: cell.bottom - box.top,
            below: row === 0
        })
    }

    return (
        <div className='space-y-4'>
            <div className='-mx-1 overflow-x-auto px-1 pb-1'>
                <div ref={wrapperRef} className='relative min-w-max' onPointerLeave={() => setHover(null)}>
                    <table className='w-full border-separate border-spacing-[2px] text-xs'>
                        <caption className='sr-only'>
                            Persentase pelanggan yang masih berlangganan per bulan sejak bergabung
                        </caption>

                        <thead>
                            <tr>
                                <th scope='col' className='px-2 pb-2 text-left font-medium text-muted-foreground'>
                                    Kohort
                                </th>
                                <th scope='col' className='px-2 pb-2 text-right font-medium text-muted-foreground'>
                                    Pelanggan
                                </th>
                                {Array.from({ length: columns }, (_, column) => (
                                    <th key={column} scope='col' className='min-w-14 px-1 pb-2 text-center font-medium whitespace-nowrap text-muted-foreground'>
                                        Bulan {column}
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {rows.map((row, rowIndex) => (
                                <tr key={row.key}>
                                    <th scope='row' className='px-2 py-1 text-left font-medium whitespace-nowrap text-foreground'>
                                        {row.label}
                                    </th>
                                    <td className='px-2 py-1 text-right whitespace-nowrap text-muted-foreground tabular-nums'>
                                        {formatNumber(row.size)}
                                    </td>
                                    {Array.from({ length: columns }, (_, column) => {
                                        const value = row.retention[column]

                                        if (value === undefined) {
                                            return (
                                                <td key={column} className='rounded-[4px] bg-muted/40'>
                                                    <span className='sr-only'>Belum tersedia</span>
                                                </td>
                                            )
                                        }

                                        return (
                                            <td
                                                key={column}
                                                className='h-9 rounded-[4px] px-1 text-center font-medium text-foreground tabular-nums transition-shadow hover:ring-2 hover:ring-foreground/40'
                                                style={{ background: cellBackground(value, color) }}
                                                onPointerEnter={(event) => onEnter(event.currentTarget, rowIndex, column)}
                                            >
                                                {formatPercent(value, value >= 99.95 ? 0 : 1)}
                                            </td>
                                        )
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    {hover && hovered && hoveredValue !== undefined && (
                        <div
                            className='pointer-events-none absolute z-10 w-max max-w-[15rem] rounded-lg border bg-popover px-3 py-2 text-xs text-popover-foreground shadow-lg'
                            style={{
                                left: hover.x,
                                top: hover.below ? hover.bottom + 8 : hover.top - 8,
                                transform: hover.below ? 'translateX(-50%)' : 'translate(-50%, -100%)'
                            }}
                        >
                            <p className='font-semibold text-foreground'>
                                Kohort {hovered.label}, bulan ke-{hover.column}
                            </p>
                            <p className='mt-1 text-muted-foreground'>
                                <span className='font-semibold text-foreground tabular-nums'>{formatPercent(hoveredValue)}</span> bertahan, sekitar{' '}
                                {formatNumber(Math.round((hovered.size * hoveredValue) / 100))} dari {formatNumber(hovered.size)} pelanggan
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <div className='flex items-center gap-3 text-xs text-muted-foreground' aria-hidden='true'>
                <span>{DOMAIN_MIN}% atau kurang</span>
                <span
                    className='h-2 w-32 rounded-full sm:w-40'
                    style={{ background: `linear-gradient(to right, ${cellBackground(DOMAIN_MIN, color)}, ${cellBackground(100, color)})` }}
                />
                <span>100%</span>
            </div>
        </div>
    )
}
